import { lazy, Suspense, type ReactElement } from 'react';
import { hasVisibleStackedChartValue } from '@/features/uploads/pages/executionRunsPageCharts';
import { s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites';
import { Panel } from '@/ui/composites/Panel';
import { Box, PieChart, Text } from '@/ui/primitives';
import { formatChartDateLabel } from '@/ui/primitives/chartDateLabel';
import { LineChart } from '@/ui/primitives/LineChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import { MetricChartPanel } from './MetricChartPanel';
import { MiniMetricChartHeader } from './MiniMetricChartHeader';
import {
  type StackedMetricsMiniPanelsLayout,
  type StackedMetricsStatusChartPanelLayout,
} from './StackedMetricsStatusChartSection.layout';
import {
  formatStackedStatusChartLabel,
  renderMiniMetricEmptyState,
  renderMiniMetricHeaderLoadingState,
  renderMiniMetricLoadingState,
  renderStatusPieLoadingState,
} from './StackedMetricsStatusChartSection.states';
import { StatusChartPanel } from './StatusChartPanel';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from './useMetricsStatusChartPanelModel';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const StackedBarChart = lazy(async () => ({
  default: (await import('@/ui/primitives/StackedBarChart')).StackedBarChart,
}));

type StatusPieDatum = {
  name: string;
  value: number;
  color?: string;
};

type StatusPieBreakdownPanelProps = {
  loading: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  miniPanels: StackedMetricsMiniPanelsLayout;
  headerToChartGap: number;
  miniMetricEmptyTitle: string;
  statusPieData: StatusPieDatum[];
  statusLegend: ReactElement | null;
};

export const StatusPieBreakdownPanel = ({
  loading,
  piePanelHeight,
  pieChartVisualHeight,
  miniPanels,
  headerToChartGap,
  miniMetricEmptyTitle,
  statusPieData,
  statusLegend,
}: StatusPieBreakdownPanelProps): ReactElement => (
  <Panel
    padding="none"
    gap="none"
    contentGap="none"
    sx={{
      minWidth: 0,
      height: piePanelHeight,
      backgroundColor: 'background.paper',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: miniPanels.headerPaddingX,
          pt: s('lg'),
          pb: miniPanels.headerPaddingBottom,
          minHeight: `${miniPanels.headerHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Text
          role="label"
          sx={{
            color: 'text.secondary',
            fontSize: 'subtitle1.fontSize',
            lineHeight: 1.4,
          }}
        >
          Status Breakdown
        </Text>
      </Box>
      <Box
        sx={{
          px: s('lg'),
          pb: s('lg'),
          flex: 1,
          minHeight: 0,
          display: 'grid',
          alignItems: 'stretch',
          boxSizing: 'border-box',
        }}
      >
        {loading ? (
          renderStatusPieLoadingState(pieChartVisualHeight)
        ) : statusPieData.length > 0 ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: s('xs'),
              minHeight: 0,
            }}
          >
            <PieChart
              data={statusPieData}
              height={pieChartVisualHeight}
              innerRadius={38}
              showLegend={false}
            />
            {statusLegend}
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: pieChartVisualHeight,
              minHeight: pieChartVisualHeight,
              display: 'grid',
              placeItems: 'center',
              transform: `translateY(${headerToChartGap}px)`,
            }}
          >
            <ChartEmptyState title={miniMetricEmptyTitle} />
          </Box>
        )}
      </Box>
    </Box>
  </Panel>
);

type StatusStackedChartPanelProps<TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  availableTags: string[];
  loading: boolean;
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  statusChartVisualHeight: number;
  resolvedStatusChartLegend: ReactElement | null;
  statusEmptyTitle: string;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  showStatusChartSkeleton: boolean;
  suppressAnimation: boolean;
};

export const StatusStackedChartPanel = function StatusStackedChartPanel<TStatus extends string>({
  title,
  searchAriaLabel,
  statusOptions,
  statusFilterLabel,
  availableTags,
  loading,
  toolbarState,
  statusPanel,
  statusChartVisualHeight,
  resolvedStatusChartLegend,
  statusEmptyTitle,
  statusChartData,
  showStatusChartSkeleton,
  suppressAnimation,
}: StatusStackedChartPanelProps<TStatus>): ReactElement {
  return (
    <StatusChartPanel<TStatus>
      title={title}
      showToolbarControls={false}
      searchAriaLabel={searchAriaLabel}
      height={statusPanel.height}
      chartHeight={statusPanel.chartHeight}
      panelPadding={statusPanel.panelPadding}
      panelContentGap={statusPanel.panelContentGap}
      headerHeight={statusPanel.headerHeight}
      headerTitlePaddingLeft={statusPanel.headerTitlePaddingLeft}
      headerControlsPaddingRight={statusPanel.headerControlsPaddingRight}
      emptyTitle={statusEmptyTitle}
      emptyBody="Try adjusting filters or search."
      onDebouncedSearchChange={toolbarState.setSearch}
      filterStatuses={toolbarState.selectedStatuses}
      statusOptions={statusOptions}
      onFilterStatusesChange={toolbarState.setSelectedStatuses}
      filterTags={toolbarState.selectedTags}
      onFilterTagsChange={toolbarState.setSelectedTags}
      dateQuickFilter={toolbarState.dateQuickFilter}
      onDateQuickFilterChange={toolbarState.onDateQuickFilterChange}
      fromDate={toolbarState.fromDate}
      toDate={toolbarState.toDate}
      onFromDateChange={(next) => {
        toolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
      }}
      onToDateChange={(next) => {
        toolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
      }}
      availableTags={availableTags}
      statusFilterLabel={statusFilterLabel}
      headerRightSlot={resolvedStatusChartLegend}
    >
      {loading || showStatusChartSkeleton ? (
        <StackedBarChartSkeleton height={statusChartVisualHeight} />
      ) : statusChartData &&
        hasVisibleStackedChartValue(statusChartData.data, statusChartData.series) ? (
        <Suspense fallback={<StackedBarChartSkeleton height={statusChartVisualHeight} />}>
          <StackedBarChart
            data={statusChartData.data}
            series={statusChartData.series}
            height={statusChartVisualHeight}
            xAxisLabelFormatter={formatStackedStatusChartLabel}
            tooltipLabelFormatter={formatChartDateLabel}
            marginLeft={statusPanel.chartMargins.left}
            marginRight={statusPanel.chartMargins.right}
            marginTop={statusPanel.headerToChartGap}
            marginBottom={statusPanel.chartMargins.bottom}
            yAxisWidth={statusPanel.yAxisWidth}
            yAxisAutoWidth={statusPanel.yAxisAutoWidth}
            yAxisAutoWidthPadding={statusPanel.yAxisAutoWidthPadding}
            axisStrokeWidth={statusPanel.axisStrokeWidth}
            barCornerRadius={statusPanel.barCornerRadius}
            yAxisLabelOffset={statusPanel.yAxisLabelOffset}
            hideXAxisTicks={statusPanel.hideXAxisTicks}
            xAxisLabelOffset={statusPanel.xAxisLabelOffset}
            showHorizontalGrid={false}
            hideXAxisLine={false}
            skipAnimation={suppressAnimation}
          />
        </Suspense>
      ) : (
        <Box
          sx={{
            height: statusChartVisualHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChartEmptyState title={statusEmptyTitle} body="Try adjusting filters or search." />
        </Box>
      )}
    </StatusChartPanel>
  );
};

type MiniMetricPanelsProps = {
  loading: boolean;
  model: MetricsStatusChartPanelDisplayModel;
  miniPanels: StackedMetricsMiniPanelsLayout;
  miniMetricEmptyTitle: string;
  scope: MetricsScope;
  hasMetricChartValue: {
    passRate: boolean;
    frequency: boolean;
    duration: boolean;
  };
};

export const MiniMetricPanels = ({
  loading,
  model,
  miniPanels,
  miniMetricEmptyTitle,
  scope,
  hasMetricChartValue,
}: MiniMetricPanelsProps): ReactElement => {
  const metricChart = (summary: ReactElement, chartNode: ReactElement): ReactElement => (
    <MetricChartPanel
      header={summary}
      height={miniPanels.height}
      headerHeight={miniPanels.headerHeight}
      panelPaddingTop={miniPanels.panelPaddingTop}
      panelPaddingBottom={miniPanels.panelPaddingBottom}
      headerPaddingX={miniPanels.headerPaddingX}
      headerPaddingTop={miniPanels.headerPaddingTop}
      headerPaddingBottom={miniPanels.headerPaddingBottom}
      contentPaddingX={miniPanels.contentPaddingX}
      contentPaddingTop={miniPanels.contentPaddingTop}
      contentPaddingBottom={miniPanels.contentPaddingBottom}
    >
      <Box
        sx={{
          px: miniPanels.chartWrapperPaddingX,
          pt: miniPanels.chartWrapperPaddingTop,
          pb: miniPanels.chartWrapperPaddingBottom,
        }}
      >
        {chartNode}
      </Box>
    </MetricChartPanel>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          lg: 'minmax(0, 1fr)',
        },
        columnGap: miniPanels.columnGap,
        rowGap: miniPanels.rowGap,
        width: '100%',
        minWidth: 0,
      }}
    >
      {metricChart(
        loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title="Pass Rate"
            summary={hasMetricChartValue.passRate ? model.passRateSummary : null}
          />
        ),
        loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.passRateChartData && hasMetricChartValue.passRate ? (
          <LineChart
            data={model.passRateChartData}
            series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            showLegend={false}
            showHorizontalGrid={false}
            hideXAxisLine={false}
            yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
            yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            showPoints={model.passRateChartData.length === 1}
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
      {metricChart(
        loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title="Frequency"
            summary={hasMetricChartValue.frequency ? model.frequencySummary : null}
          />
        ),
        loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.frequencyChartData && hasMetricChartValue.frequency ? (
          <LineChart
            data={model.frequencyChartData}
            series={[
              {
                key: 'frequency',
                label: scope === 'executionRun' ? 'Runs' : '',
                color: 'var(--color-pink)',
              },
            ]}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            showLegend={false}
            showHorizontalGrid={false}
            hideXAxisLine={false}
            yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
            yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            showPoints={model.frequencyChartData.length === 1}
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
      {metricChart(
        loading ? (
          renderMiniMetricHeaderLoadingState()
        ) : (
          <MiniMetricChartHeader
            title="Avg. Duration"
            summary={hasMetricChartValue.duration ? model.durationSummary : null}
          />
        ),
        loading ? (
          renderMiniMetricLoadingState(miniPanels.chartHeight)
        ) : model.durationChartData && hasMetricChartValue.duration ? (
          <LineChart
            data={model.durationChartData}
            series={[{ key: 'duration', label: '' }]}
            height={miniPanels.chartHeight}
            marginLeft={miniPanels.chartMargins.left}
            marginRight={miniPanels.chartMargins.right}
            marginTop={miniPanels.chartMargins.top}
            marginBottom={miniPanels.chartMargins.bottom}
            yAxisWidth={miniPanels.yAxisWidth}
            axisStrokeWidth={miniPanels.axisStrokeWidth}
            showLegend={false}
            showHorizontalGrid={false}
            hideXAxisLine={false}
            yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
            yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            showPoints={model.durationChartData.length === 1}
          />
        ) : (
          renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
        ),
      )}
    </Box>
  );
};
